import { useState, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Signature,
  Type,
  Calendar as CalIcon,
  Check,
  User as UserIcon,
  Mail,
  Phone,
  Hash,
  Trash2,
  Plus,
  Send,
  Save,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Users,
  Upload,
  Grid3x3,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import PdfCanvas from "@/components/PdfCanvas";
import { toast } from "sonner";

const FIELD_TYPES = [
  { type: "signature", label: "Signature", icon: Signature, w: 180, h: 60 },
  { type: "initials", label: "Initials", icon: Hash, w: 80, h: 40 },
  { type: "full_name", label: "Full name", icon: UserIcon, w: 180, h: 32 },
  { type: "date", label: "Date", icon: CalIcon, w: 140, h: 32 },
  { type: "text", label: "Text", icon: Type, w: 320, h: 60 },
  { type: "email", label: "Email", icon: Mail, w: 200, h: 32 },
  { type: "phone", label: "Phone", icon: Phone, w: 160, h: 32 },
  { type: "checkbox", label: "Checkbox", icon: Check, w: 28, h: 28 },
];

const COLORS = ["#059669", "#0284c7", "#d946ef", "#f97316", "#eab308", "#ef4444"];

function recipientColor(index) {
  return COLORS[index % COLORS.length];
}

function uid() {
  return "f_" + Math.random().toString(36).slice(2, 10);
}

/**
 * Auto-sizing textarea inside a field marker.
 * - On mount only: if value already has content (e.g., reload from server), measure
 *   scrollHeight ONCE and grow the parent marker to fit (via onCommit).
 * - On user input: measure scrollHeight and onCommit(value, max(measured, f.height)).
 *   `Math.max` ensures any manually-set size from the resize handle is respected — the
 *   field never shrinks below the user's manual choice.
 * - The textarea itself is `height: 100%` (set in CSS) so it always fills the marker.
 *   We only briefly toggle `height: auto` to measure scrollHeight, then restore.
 * No setState happens inside a ref callback, so no infinite render loop.
 */
function TextFieldEditor({ f, color, placeholder, testId, onCommit }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !(f.value || "").length) return;
    const prev = el.style.height;
    el.style.height = "auto";
    const measured = Math.max(32, el.scrollHeight);
    el.style.height = prev;
    if (measured > f.height) {
      onCommit(f.value, measured);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    const prev = e.target.style.height;
    e.target.style.height = "auto";
    const measured = Math.max(32, e.target.scrollHeight);
    e.target.style.height = prev;
    onCommit(v, Math.max(measured, f.height));
  };

  return (
    <textarea
      ref={ref}
      value={f.value || ""}
      placeholder={placeholder}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onDragStart={(e) => e.preventDefault()}
      draggable={false}
      spellCheck={false}
      className="inline-edit"
      data-testid={testId}
      style={{
        color,
        resize: "none",
        overflow: "hidden",
        whiteSpace: "pre-wrap",
        lineHeight: 1.4,
        padding: "4px 6px",
      }}
    />
  );
}

export default function PrepareDocument() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [fields, setFields] = useState([]);
  const [signingMode, setSigningMode] = useState("sequential");
  const [message, setMessage] = useState("");
  const [activeRecipient, setActiveRecipient] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [pendingFieldType, setPendingFieldType] = useState(null);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const overlayRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState("");
  const [bulkReplace, setBulkReplace] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [mobilePane, setMobilePane] = useState("doc"); // doc | fields | recipients
  const [showGrid, setShowGrid] = useState(() => {
    try {
      return localStorage.getItem("attestra.grid") === "1";
    } catch {
      return false;
    }
  });
  const GRID = 10;
  const snap = (n) => (showGrid ? Math.round(n / GRID) * GRID : n);
  const toggleGrid = () => {
    setShowGrid((g) => {
      const next = !g;
      try {
        localStorage.setItem("attestra.grid", next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/documents/${id}`);
        setDoc(data);
        const incoming = data.recipients || [];
        setRecipients(incoming);
        setFields(data.fields || []);
        setSigningMode(data.signing_mode || "sequential");
        setMessage(data.message || "");
        setNumPages(data.page_count || 1);
        if (incoming.length) {
          setActiveRecipient(incoming[0].id);
        } else if (data.status === "draft") {
          // Auto-add a first signer so the field palette is immediately usable.
          // Pre-fill with the current user's name/email — they can change it before sending.
          const seed = {
            id: "r_" + Math.random().toString(36).slice(2, 10),
            name: user?.name || "",
            email: "",
            role: "signer",
            order: 1,
            verification_method: "none",
          };
          setRecipients([seed]);
          setActiveRecipient(seed.id);
          setDirty(true);
        }
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/app/documents");
      }
    })();
  }, [id, navigate, user]);

  // Escape key → deselect any selected field + cancel any pending field placement.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSelectedFieldId(null);
        setPendingFieldType(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const addRecipient = () => {
    const idx = recipients.length + 1;
    const r = {
      id: "r_" + Math.random().toString(36).slice(2, 10),
      name: "",
      email: "",
      role: "signer",
      order: idx,
    };
    setRecipients([...recipients, r]);
    if (!activeRecipient) setActiveRecipient(r.id);
  };

  const updateRecipient = (rid, patch) => {
    setRecipients(recipients.map((r) => (r.id === rid ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const removeRecipient = (rid) => {
    setRecipients(recipients.filter((r) => r.id !== rid));
    setFields(fields.filter((f) => f.recipient_id !== rid));
    if (activeRecipient === rid) setActiveRecipient(null);
    setDirty(true);
  };

  const handlePageClick = (e, dims) => {
    // Click on empty page area → deselect any selected field
    if (!pendingFieldType) {
      setSelectedFieldId(null);
      return;
    }
    if (!activeRecipient) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const spec = FIELD_TYPES.find((f) => f.type === pendingFieldType);
    if (!spec) return;
    const field = {
      id: uid(),
      type: pendingFieldType,
      page: pageNumber,
      x: snap(Math.max(0, x - spec.w / 2)),
      y: snap(Math.max(0, y - spec.h / 2)),
      width: spec.w,
      height: spec.h,
      rendered_viewport_width: 820,
      recipient_id: activeRecipient,
      required: true,
    };
    setFields([...fields, field]);
    setSelectedFieldId(field.id);
    setPendingFieldType(null);
    setDirty(true);
  };

  // ---- Drag-and-drop: palette → canvas ----
  const handlePagePointerDrop = (e, paletteType) => {
    if (!activeRecipient) {
      toast.error("Add a recipient first — use the panel on the right.");
      setMobilePane("recipients");
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const spec = FIELD_TYPES.find((f) => f.type === paletteType);
    if (!spec) return;
    const field = {
      id: uid(),
      type: paletteType,
      page: pageNumber,
      x: snap(Math.max(0, x - spec.w / 2)),
      y: snap(Math.max(0, y - spec.h / 2)),
      width: spec.w,
      height: spec.h,
      rendered_viewport_width: 820,
      recipient_id: activeRecipient,
      required: true,
    };
    setFields((prev) => [...prev, field]);
    setSelectedFieldId(field.id);
    setDirty(true);
  };

  // ---- Drag existing field within a page ----
  const dragRef = useRef(null);
  const handleFieldDragStart = (e, f) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = {
      id: f.id,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    // a transparent drag image keeps the original positioned via state
    const img = new Image();
    img.src =
      "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciLz4=";
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setDragImage(img, 0, 0);
    } catch {
      /* no-op */
    }
  };

  const handleCanvasDragOver = (e) => {
    if (e.dataTransfer.types.includes("application/x-attestra-field")) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    } else if (dragRef.current) {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
    }
  };

  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const paletteType = e.dataTransfer.getData("application/x-attestra-field");
    if (paletteType) {
      handlePagePointerDrop(e, paletteType);
      return;
    }
    if (dragRef.current) {
      const rect = e.currentTarget.getBoundingClientRect();
      const newX = e.clientX - rect.left - dragRef.current.offsetX;
      const newY = e.clientY - rect.top - dragRef.current.offsetY;
      const id = dragRef.current.id;
      setFields((prev) =>
        prev.map((f) =>
          f.id === id ? { ...f, x: snap(Math.max(0, newX)), y: snap(Math.max(0, newY)) } : f,
        ),
      );
      setDirty(true);
      dragRef.current = null;
    }
  };

  const updateField = (fid, patch) => {
    setFields(fields.map((f) => (f.id === fid ? { ...f, ...patch } : f)));
    setDirty(true);
  };

  const duplicateField = (fid) => {
    const src = fields.find((f) => f.id === fid);
    if (!src) return;
    const copy = { ...src, id: uid(), x: src.x + 16, y: src.y + 16 };
    setFields([...fields, copy]);
    setSelectedFieldId(copy.id);
    setDirty(true);
  };

  const removeField = (fid) => {
    setFields(fields.filter((f) => f.id !== fid));
    if (selectedFieldId === fid) setSelectedFieldId(null);
    setDirty(true);
  };

  const updateFieldValue = (fid, value) => {
    setFields((prev) => prev.map((f) => (f.id === fid ? { ...f, value } : f)));
    setDirty(true);
  };

  // Update value + auto-grown height together (avoids double re-renders for textarea autosize)
  const updateFieldValueAndSize = (fid, value, height) => {
    setFields((prev) =>
      prev.map((f) => (f.id === fid ? { ...f, value, height } : f)),
    );
    setDirty(true);
  };

  const updateFieldSize = (fid, width, height) => {
    setFields((prev) =>
      prev.map((f) => (f.id === fid ? { ...f, width, height } : f)),
    );
    setDirty(true);
  };

  // Drag corner of a field to resize. Snaps to grid when grid is on.
  // For multi-line text fields, the minimum height is clamped to the content's scrollHeight
  // so users cannot accidentally hide content by shrinking the field.
  const handleResizeStart = (e, f) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = f.width;
    const startH = f.height;
    // For text fields, find the inner textarea to use scrollHeight as a hard min.
    const markerEl = e.currentTarget.parentElement;
    const textarea = markerEl?.querySelector("textarea");
    const contentMinH = textarea ? Math.max(32, textarea.scrollHeight) : 24;
    const onMove = (ev) => {
      const newW = Math.max(40, snap(startW + (ev.clientX - startX)));
      const newH = Math.max(contentMinH, snap(startH + (ev.clientY - startY)));
      updateFieldSize(f.id, newW, newH);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const buildPayload = () => {
    // Validate recipients
    for (const r of recipients) {
      if (!r.name || !r.email) {
        throw new Error("Each recipient must have a name and email.");
      }
    }
    return {
      recipients,
      fields,
      signing_mode: signingMode,
      message,
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      await api.patch(`/documents/${id}`, payload);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response ? formatApiError(e) : e.message);
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    if (!recipients.length) return toast.error("Add at least one recipient.");
    if (!fields.length) return toast.error("Place at least one field.");
    setSending(true);
    try {
      const payload = buildPayload();
      await api.patch(`/documents/${id}`, payload);
      await api.post(`/documents/${id}/send`, { message });
      toast.success("Document sent!");
      navigate(`/app/documents/${id}`);
    } catch (e) {
      toast.error(e?.response ? formatApiError(e) : e.message);
    } finally {
      setSending(false);
    }
  };

  const bulkImport = async () => {
    if (!bulkCsv.trim()) return toast.error("Paste recipients first.");
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/documents/${id}/recipients/import`, {
        csv: bulkCsv,
        replace: bulkReplace,
      });
      toast.success(`Imported ${data.imported} recipient(s)`);
      // reload from server
      const fresh = await api.get(`/documents/${id}`);
      setRecipients(fresh.data.recipients || []);
      if ((fresh.data.recipients || []).length && !activeRecipient) {
        setActiveRecipient(fresh.data.recipients[0].id);
      }
      setBulkOpen(false);
      setBulkCsv("");
      setBulkReplace(false);
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const visibleFields = useMemo(
    () => fields.filter((f) => f.page === pageNumber),
    [fields, pageNumber],
  );

  if (!doc) {
    return <div className="p-10 text-sm text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-[100dvh] flex flex-col bg-slate-50" data-testid="prepare-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-6 h-14 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/documents")}
            className="gap-1 text-slate-600 px-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          <div className="min-w-0">
            <div className="text-[10px] sm:text-xs text-slate-500">Preparing</div>
            <div className="text-xs sm:text-sm font-semibold truncate max-w-[140px] sm:max-w-none">
              {doc.title}
            </div>
          </div>
          {dirty ? (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-md border border-amber-200 dark:border-amber-800">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              Unsaved changes
            </span>
          ) : lastSavedAt ? (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-emerald-700 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Saved {lastSavedAt.toLocaleTimeString()}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <Button
            variant={showGrid ? "default" : "outline"}
            size="sm"
            onClick={toggleGrid}
            data-testid="prepare-grid-toggle"
            className={
              showGrid
                ? "bg-slate-900 hover:bg-slate-800 text-white"
                : ""
            }
            title="Toggle alignment grid (snaps to 10px)"
          >
            <Grid3x3 className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Grid</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={save}
            disabled={saving}
            data-testid="prepare-save-btn"
          >
            <Save className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">{saving ? "Saving…" : "Save"}</span>
          </Button>
          <Button
            size="sm"
            onClick={send}
            disabled={sending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="prepare-send-btn"
          >
            <Send className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">{sending ? "Sending…" : "Send"}</span>
          </Button>
        </div>
      </div>

      {/* Mobile pane switcher */}
      <div className="md:hidden bg-white border-b border-slate-200 px-2 py-2 flex items-center gap-1 shrink-0">
        {[
          { v: "fields", label: "Fields" },
          { v: "doc", label: "Document" },
          { v: "recipients", label: "Recipients" },
        ].map((p) => (
          <button
            key={p.v}
            onClick={() => setMobilePane(p.v)}
            data-testid={`prepare-pane-${p.v}`}
            className={`flex-1 px-3 py-1.5 rounded-md text-xs font-medium transition ${
              mobilePane === p.v
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Workspace */}
      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        {/* Left: Field palette */}
        <div
          className={`${
            mobilePane === "fields" ? "block" : "hidden"
          } md:block col-span-12 md:col-span-2 bg-white border-r border-slate-200 p-3 overflow-y-auto`}
        >
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2 px-1">
            Fields
          </div>
          {!activeRecipient && (
            <div
              className="mb-3 rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-2.5 text-[11px] leading-relaxed text-amber-900"
              data-testid="palette-empty-recipient"
            >
              <div className="font-semibold mb-1 text-amber-800">Add a recipient first</div>
              <div className="text-amber-700">
                Each field belongs to a signer. Click{" "}
                <button
                  type="button"
                  onClick={() => {
                    addRecipient();
                    setMobilePane("recipients");
                  }}
                  className="underline font-medium text-amber-900 hover:text-amber-700"
                  data-testid="palette-add-recipient-btn"
                >
                  Add signer
                </button>{" "}
                to start placing fields.
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            {FIELD_TYPES.map((f) => {
              const Icon = f.icon;
              const active = pendingFieldType === f.type;
              const disabled = !activeRecipient;
              return (
                <button
                  key={f.type}
                  disabled={disabled}
                  draggable={!disabled}
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = "copy";
                    e.dataTransfer.setData("application/x-attestra-field", f.type);
                  }}
                  onClick={() => setPendingFieldType(active ? null : f.type)}
                  data-testid={`field-palette-${f.type}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm border transition cursor-grab active:cursor-grabbing ${
                    active
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 hover:bg-slate-50 hover:border-emerald-300"
                  } ${disabled ? "opacity-40 cursor-not-allowed bg-slate-50" : ""}`}
                >
                  <Icon className="h-4 w-4" />
                  {f.label}
                </button>
              );
            })}
          </div>
          {pendingFieldType && (
            <div className="mt-3 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded">
              Click — or drag — anywhere on the document to place.
            </div>
          )}
          {!pendingFieldType && activeRecipient && (
            <div className="mt-3 text-[11px] text-slate-500">
              Tip: drag a field type onto the page to place it precisely.
            </div>
          )}

          <div className="mt-6 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium mb-2 px-1">
            Signing order
          </div>
          <div className="px-1">
            <Select value={signingMode} onValueChange={setSigningMode}>
              <SelectTrigger className="h-9" data-testid="signing-mode-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sequential">Sequential</SelectItem>
                <SelectItem value="parallel">Parallel</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Middle: PDF */}
        <div
          className={`${
            mobilePane === "doc" ? "block" : "hidden"
          } md:block col-span-12 md:col-span-7 overflow-auto p-3 sm:p-6`}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Button
              variant="outline"
              size="sm"
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber(Math.max(1, pageNumber - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="text-xs font-mono text-slate-600">
              Page {pageNumber} / {numPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={pageNumber >= numPages}
              onClick={() => setPageNumber(Math.min(numPages, pageNumber + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <div className="ml-4 inline-flex items-center bg-slate-100 dark:bg-slate-800 rounded-md p-0.5">
              <button
                className="px-2 py-1 text-xs font-medium hover:bg-white dark:hover:bg-slate-900 rounded"
                onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                data-testid="zoom-out"
              >
                −
              </button>
              <span className="px-2 text-xs font-mono w-12 text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                className="px-2 py-1 text-xs font-medium hover:bg-white dark:hover:bg-slate-900 rounded"
                onClick={() => setZoom(Math.min(2, zoom + 0.1))}
                data-testid="zoom-in"
              >
                +
              </button>
            </div>
          </div>

          <div
            ref={overlayRef}
            onDragOver={handleCanvasDragOver}
            onDrop={handleCanvasDrop}
            className={showGrid ? "attestra-grid" : ""}
          >
            <PdfCanvas
              fileUrl={doc.file_data_url}
              pageNumber={pageNumber}
              maxWidth={Math.round(820 * zoom)}
              onLoadPages={(n) => setNumPages(n)}
              onPageClick={handlePageClick}
              renderOverlay={() => (
                <>
                  {visibleFields.map((f) => {
                    const rIdx = recipients.findIndex((r) => r.id === f.recipient_id);
                    const color = recipientColor(rIdx >= 0 ? rIdx : 0);
                    const spec = FIELD_TYPES.find((x) => x.type === f.type);
                    const Icon = spec?.icon || Type;
                    const editable = ["text", "date", "full_name", "email", "phone"].includes(
                      f.type,
                    );
                    const isMultiline = f.type === "text";
                    const inputType = f.type === "date" ? "date" : f.type === "email" ? "email" : "text";
                    return (
                      <div
                        key={f.id}
                        className={`field-marker flex items-center justify-center ${
                          selectedFieldId === f.id ? "ring-2 ring-emerald-500" : ""
                        }`}
                        style={{
                          left: f.x,
                          top: f.y,
                          width: f.width,
                          height: f.height,
                          borderColor: color,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFieldId(f.id);
                        }}
                        data-testid={`field-${f.type}-${f.id}`}
                      >
                        <span
                          className="drag-handle"
                          draggable
                          onDragStart={(e) => handleFieldDragStart(e, f)}
                          title="Drag to move"
                          data-testid={`field-drag-${f.id}`}
                          style={{ background: color }}
                        >
                          <svg viewBox="0 0 8 14" width="10" height="14" fill="currentColor" aria-hidden="true">
                            <circle cx="2" cy="2" r="1.2"/><circle cx="6" cy="2" r="1.2"/>
                            <circle cx="2" cy="7" r="1.2"/><circle cx="6" cy="7" r="1.2"/>
                            <circle cx="2" cy="12" r="1.2"/><circle cx="6" cy="12" r="1.2"/>
                          </svg>
                        </span>
                        {isMultiline ? (
                          <RichTextEditor
                            value={f.value || ""}
                            color={color}
                            testId={`field-input-${f.id}`}
                            onChange={(html) => updateFieldValue(f.id, html)}
                            onHeight={(h) => {
                              if (h && h > (f.height || 0)) {
                                updateFieldSize(f.id, f.width, h + 8);
                              }
                            }}
                          />
                        ) : editable ? (
                          <input
                            type={inputType}
                            value={f.value || ""}
                            placeholder={spec?.label}
                            onChange={(e) => updateFieldValue(f.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onDragStart={(e) => e.preventDefault()}
                            draggable={false}
                            className="inline-edit"
                            data-testid={`field-input-${f.id}`}
                            style={{ color }}
                          />
                        ) : (
                          <div
                            className="text-[11px] font-medium truncate px-1 flex items-center gap-1 pointer-events-none"
                            style={{ color }}
                          >
                            <Icon className="h-3 w-3" />
                            {f.label || spec?.label}
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            duplicateField(f.id);
                          }}
                          className="absolute -left-2 -top-2 bg-white border border-slate-200 rounded-full h-5 w-5 flex items-center justify-center shadow-sm hover:bg-slate-50"
                          title="Duplicate"
                          data-testid={`field-duplicate-${f.id}`}
                        >
                          <Plus className="h-3 w-3 text-slate-600" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeField(f.id);
                          }}
                          className="absolute -right-2 -top-2 bg-white border border-slate-200 rounded-full h-5 w-5 flex items-center justify-center shadow-sm hover:bg-red-50"
                          data-testid={`field-remove-${f.id}`}
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </button>
                        <span
                          className="resize-handle"
                          onMouseDown={(e) => handleResizeStart(e, f)}
                          onClick={(e) => e.stopPropagation()}
                          title="Drag to resize"
                          data-testid={`field-resize-${f.id}`}
                        />
                      </div>
                    );
                  })}
                </>
              )}
            />
          </div>
        </div>

        {/* Right: Recipients */}
        <div
          className={`${
            mobilePane === "recipients" ? "block" : "hidden"
          } md:block col-span-12 md:col-span-3 bg-white border-l border-slate-200 p-4 overflow-y-auto`}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-slate-600" />
              <div className="text-sm font-semibold">Recipients</div>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkOpen(true)}
                data-testid="bulk-import-btn"
                title="Bulk import from CSV"
              >
                <Upload className="h-4 w-4 mr-1" /> Import
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={addRecipient}
                data-testid="add-recipient-btn"
              >
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {recipients.length === 0 && (
            <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-md p-4 text-center">
              Add at least one recipient to place fields.
            </div>
          )}

          <div className="space-y-3">
            {recipients.map((r, i) => {
              const color = recipientColor(i);
              const active = activeRecipient === r.id;
              return (
                <div
                  key={r.id}
                  className={`rounded-lg border p-3 cursor-pointer ${
                    active ? "border-emerald-500 ring-1 ring-emerald-500/30" : "border-slate-200"
                  }`}
                  onClick={() => setActiveRecipient(r.id)}
                  data-testid={`recipient-card-${r.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2.5 w-2.5 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      <div className="text-xs font-medium text-slate-700">
                        Signer {i + 1}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeRecipient(r.id);
                      }}
                      className="p-1 rounded hover:bg-slate-100"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  </div>
                  <Input
                    placeholder="Name"
                    value={r.name}
                    onChange={(e) => updateRecipient(r.id, { name: e.target.value })}
                    className="h-9 text-sm mb-2"
                    data-testid={`recipient-name-${r.id}`}
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={r.email}
                    onChange={(e) => updateRecipient(r.id, { email: e.target.value })}
                    className="h-9 text-sm mb-2"
                    data-testid={`recipient-email-${r.id}`}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Select
                      value={r.role}
                      onValueChange={(v) => updateRecipient(r.id, { role: v })}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="signer">Signer</SelectItem>
                        <SelectItem value="approver">Approver</SelectItem>
                        <SelectItem value="form_filler">Form filler</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="1"
                      value={r.order}
                      onChange={(e) =>
                        updateRecipient(r.id, { order: parseInt(e.target.value) || 1 })
                      }
                      className="h-9 text-xs text-center"
                    />
                  </div>
                  <Select
                    value={r.verification_method || "none"}
                    onValueChange={(v) => updateRecipient(r.id, { verification_method: v })}
                  >
                    <SelectTrigger className="h-9 text-xs mt-2" data-testid={`verif-${r.id}`}>
                      <SelectValue placeholder="Verification" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No verification</SelectItem>
                      <SelectItem value="email">Email verification</SelectItem>
                      <SelectItem value="otp">OTP verification</SelectItem>
                    </SelectContent>
                  </Select>
                  <Textarea
                    placeholder="Private message to this recipient (optional)"
                    value={r.private_message || ""}
                    onChange={(e) => updateRecipient(r.id, { private_message: e.target.value })}
                    className="text-xs mt-2"
                    rows={2}
                    data-testid={`pmsg-${r.id}`}
                  />
                  <div className="mt-2 text-[10px] font-mono text-slate-500">
                    {fields.filter((f) => f.recipient_id === r.id).length} fields
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6">
            <Label className="text-xs font-medium text-slate-700 dark:text-slate-300">
              Message to signers
            </Label>
            <Textarea
              placeholder="Optional personal message…"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setDirty(true);
              }}
              className="mt-1.5 text-sm"
              rows={3}
            />
          </div>

          {/* Field properties */}
          {selectedFieldId && (() => {
            const sf = fields.find((f) => f.id === selectedFieldId);
            if (!sf) return null;
            return (
              <div
                className="mt-6 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-900/10 p-4"
                data-testid="field-properties-panel"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-400 font-medium">
                    Field properties
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedFieldId(null)}
                    className="h-6 w-6 rounded-md inline-flex items-center justify-center text-slate-500 hover:bg-emerald-100 hover:text-slate-900"
                    title="Deselect (Esc)"
                    data-testid="deselect-field-btn"
                  >
                    ×
                  </button>
                </div>
                <Label className="text-xs">Label</Label>
                <Input
                  value={sf.label || ""}
                  onChange={(e) => updateField(sf.id, { label: e.target.value })}
                  placeholder="e.g. Sign here"
                  className="h-9 text-xs mt-1"
                  data-testid="field-prop-label"
                />
                <Label className="text-xs mt-3 block">Placeholder</Label>
                <Input
                  value={sf.placeholder || ""}
                  onChange={(e) => updateField(sf.id, { placeholder: e.target.value })}
                  className="h-9 text-xs mt-1"
                  data-testid="field-prop-placeholder"
                />
                <div className="flex items-center justify-between mt-3 text-xs">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={sf.required}
                      onChange={(e) => updateField(sf.id, { required: e.target.checked })}
                      data-testid="field-prop-required"
                    />
                    Required
                  </label>
                  <span className="font-mono text-[10px] text-slate-500">{sf.type}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => duplicateField(sf.id)}
                    className="h-8 text-xs flex-1"
                  >
                    Duplicate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeField(sf.id)}
                    className="h-8 text-xs flex-1 text-red-600 border-red-200"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Bulk recipient import dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import recipients from CSV</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Paste rows below. Header is optional. Supported columns:
            <span className="font-mono ml-1">name,email,role,order,company,title,phone</span>.
          </p>
          <Textarea
            value={bulkCsv}
            onChange={(e) => setBulkCsv(e.target.value)}
            rows={8}
            placeholder={"name,email,role,order\nPriya Sharma,priya@example.com,signer,1\nRahul Verma,rahul@example.com,signer,2"}
            className="font-mono text-xs"
            data-testid="bulk-csv-input"
          />
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={bulkReplace}
              onChange={(e) => setBulkReplace(e.target.checked)}
              data-testid="bulk-replace-toggle"
            />
            Replace existing recipients (otherwise append)
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={bulkImport}
              disabled={bulkBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bulk-import-confirm"
            >
              {bulkBusy ? "Importing…" : "Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
